import "../../../assets/scss/rental-card-style.scss";
import { useEffect, useRef, useState } from "react";
import {
  Button,
  ConfigProvider,
  Empty,
  Input,
  Skeleton,
  Table,
  TableProps,
} from "antd";

import { RentalCardInterface } from "../../utility/interface/rental-card";
import { getHostList } from "../../server/Hosts";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { useNavigate } from "react-router-dom";
import { chooseGPUSelectionInfo } from "../../features/selection/selectionSlice";

export const Dashboard = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  const [gpuList, setGPUList] = useState<RentalCardInterface[]>([]);
  const [searched, setSearched] = useState<string>("");
  const [filteredGPUList, setfilteredGPUList] = useState<RentalCardInterface[]>(
    []
  );

  const userState = useAppSelector((state: any) => state.user);
  const searchRef = useRef(null);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const filteredGPUListColumns: TableProps<RentalCardInterface>["columns"] = [
    {
      title: t("GPU Type"),
      dataIndex: "gpu_type",
      key: "gpu_type",
      sorter: true,
      render: (text: string, record: any) => {
        return (
          <div className="rental-table-columns">
            <span className="product-name">{record.product_name}</span>
            <span className="small-details">
              <span style={{ color: "#777777" }}>
                {record.gpu_available} {t("Available")} &nbsp;
              </span>
              <span style={{ color: "#BDBDBD" }}>
                / {record.total_gpu_count} GPUs
              </span>
            </span>
          </div>
        );
      },
    },
    {
      title: t("GPU RAM"),
      dataIndex: "gpu_ram",
      key: "gpu_ram",
      sorter: (a: any, b: any) => a.gpu_ram - b.gpu_ram,
      render: (text: string, record: any) => {
        return (
          <div className="rental-table-columns">
            <span>{text} GB</span>
            <span className="cpu-details">CPU: {record.cpu_name}</span>
          </div>
        );
      },
    },
    {
      title: t("Disk Storage"),
      dataIndex: "disk_max_available_storage",
      key: "disk_max_available_storage",
      sorter: (a: any, b: any) =>
        a.disk_max_available_storage - b.disk_max_available_storage,
      sortIcon: ({ sortOrder }) => <></>,
      render: (text) => {
        return <>{Math.ceil(text)} GB</>;
      },
    },
    {
      title: t("Effective RAM"),
      dataIndex: "effective_ram",
      key: "effective_ram",
      sorter: (a: any, b: any) => a.effective_ram - b.effective_ram,
      render: (text) => {
        return <>{text} GB</>;
      },
    },
    {
      title: t("Price"),
      dataIndex: "price",
      key: "price",
      sorter: (a: any, b: any) => a.price - b.price,

      render: (text: string, record: any) => (
        <div className="rental-table-columns">
          <div className="price-details">
            ${Number(text).toFixed(2)} / GPU / {t("hours")}
          </div>
          <button
            className="rent-btn"
            disabled={record.gpu_available === 0}
            onClick={() => {
              let data = {
                gpu_ram: record.gpu_ram,
                gpu_available: record.gpu_available,
                total_gpu_count: record.total_gpu_count,
                host_id: record.id,
                product_name: record.product_name,
                price: record.price,
                effective_ram: record.effective_ram,
                cpu_name: record.cpu_name,
                deep_learning_performance_score:
                  record.deep_learning_performance_score,
                disk_max_available_storage: record.disk_max_available_storage,
                internet_upload_speed: record.internet_upload_speed,
                internet_download_speed: record.internet_download_speed,
                teraflops: record.teraflops,
                selected_as_whole: false,
                gpu_type: record.gpu_type,
              };
              dispatch(chooseGPUSelectionInfo(data));
              navigate("/templates");
            }}
          >
            {t("Rent")}
          </button>
        </div>
      ),
    },
  ];

  useEffect(() => {
    let list = gpuList;
    if (searched !== "") {
      list = list.filter((gpu) => {
        return gpu.product_name.toLowerCase().includes(searched);
      });
    }
    setfilteredGPUList(list);
  }, [searched]);

  const onChangeInput = (e: any) => {
    const list = gpuList;
    const value = e.target.value;

    const filtered = list.filter((gpu) => {
      return gpu.product_name.toLowerCase().includes(value);
    });
    setfilteredGPUList(filtered);
    setSearched(value);
  };

  const handleMouseOver = (inputRef: any) => {
    inputRef.current.focus();
  };

  useEffect(() => {
    setLoading(true);
    fetchGPUList();
  }, []);

  const fetchGPUList = async () => {
    const response = await getHostList(1, 20);

    const data = await response.data;
    let list = [...data.hosts];
    setLoading(false);
    // setGPUList(data.host); // single source of truth
    setGPUList(list); // single source of truth
    setfilteredGPUList(list);
  };

  return (
    <>
      <div className="dashboard">
        <h3 className="main-title">{t("Rent GPUs")}</h3>
        <p className="descript">{t("Rent GPU Paragraph")}</p>
        <div className="rental-sorting">
          <div
            className="search-bar focus-card-wrapper"
            onMouseOver={() => handleMouseOver(searchRef)}
          >
            <section>
              <Input
                ref={searchRef}
                placeholder={t("Deploy GPUs in seconds")}
                defaultValue={searched}
                value={searched}
                onChange={onChangeInput}
              />
            </section>
          </div>
          <div className="explore-templates focus-card-wrapper">
            <section>
              <h3 className="explore-title">{t("Explore Templates")}</h3>
              <p className="explore-descript">
                {t("Explore Templates Descript")}
              </p>
            </section>
          </div>
        </div>
      </div>
      <h3 className="second-title">{t("All GPUs")}</h3>
      {loading && <Skeleton active />}
      {filteredGPUList && (
        <ConfigProvider
          theme={{
            components: {
              Table: {
                headerBg: "#F6F8FD",
                borderColor: "#F6F8FD",
              },
            },
          }}
        >
          <Table
            className="rental-table"
            columns={filteredGPUListColumns}
            dataSource={filteredGPUList}
            showHeader={filteredGPUList.length !== 0}
            pagination={false}
            loading={loading}
          />
        </ConfigProvider>
      )}

      {filteredGPUList.length === 0 && (
        <>
          {loading == false && (
            <div className="instance-card-none">
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={<>{t("No matching machine.")}</>}
              />
            </div>
          )}
        </>
      )}
    </>
  );
};
